import React from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { useStoreDetailsPosTypes } from '../../features/storeDetails/hooks/useStoreDetailsPosTypes';
import type { Store as StoreType } from '../../services/authService';
import { useStoreDetailsOverview } from '../../features/storeDetails/overview/hooks/useStoreDetailsOverview';
import { isGilbarcoType, isVerifoneType } from '../../features/storeDetails/overview/utils/storeDetailsPosSetupUtils';
import AgentConfigurationCard from './AgentConfigurationCard';
import { LottoBackfillDialog } from '../lottery/LottoBackfillDialog';
import LottoCredentialsDialog from './LottoCredentialsDialog';
import LottoSetupCard from './LottoSetupCard';
import StoreDetailsRenderProfiler from './StoreDetailsRenderProfiler';
import StoreInfoCard from './StoreInfoCard';
import StoreLogsDialog from './StoreLogsDialog';

type StoreDetailsDisplayProps = {
    store: StoreType | null;
    onSilentStoreRefresh?: () => Promise<StoreType | null> | StoreType | null | void;
};

const StoreDetailsDisplay: React.FC<StoreDetailsDisplayProps> = ({
    store,
    onSilentStoreRefresh,
}) => {
    const [selectedPosType, setSelectedPosType] = React.useState(store?.pos_type || '');
    const overviewState = useStoreDetailsOverview({
        selectedPosType,
        store,
        onSilentStoreRefresh,
    });
    const { posTypes } = useStoreDetailsPosTypes(Boolean(store));

    React.useEffect(() => {
        setSelectedPosType(store?.pos_type || '');
    }, [store?.id, store?.pos_type]);

    if (!store) {
        return null;
    }

    const currentPosType = selectedPosType || store.pos_type || '';
    const showSyncGamesToPos = isGilbarcoType(currentPosType) || isVerifoneType(currentPosType);

    return (
        <Box>
            <Grid container spacing={2}>
                <StoreInfoCard
                    activityDateRange={overviewState.activityDateRange}
                    onActivityDateRangeChange={overviewState.handleActivityDateRangeChange}
                    activityEventType={overviewState.activityEventType}
                    activityEventCategory={overviewState.activityEventCategory}
                    activityEventTypeOptions={overviewState.activityEventTypeOptions}
                    activityEventCategoryOptions={overviewState.activityEventCategoryOptions}
                    onActivityEventTypeChange={overviewState.handleActivityEventTypeChange}
                    onActivityEventCategoryChange={overviewState.handleActivityEventCategoryChange}
                    activityFeedLoading={overviewState.activityFeedLoading}
                    activityFeedLoadingMore={overviewState.activityFeedLoadingMore}
                    activityFeedHasMore={overviewState.activityFeedHasMore}
                    activityFeed={overviewState.activityFeed}
                    onActivityFeedScroll={overviewState.handleActivityFeedScroll}
                    getAvatarColor={overviewState.getAvatarColor}
                    getInitials={overviewState.getInitials}
                />

                <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 1 } }}>
                    <Stack spacing={2}>
                        <StoreDetailsRenderProfiler id="LottoSetupCard">
                            <LottoSetupCard
                                lottoEnabled={overviewState.lottoEnabled}
                                onLottoEnabledChange={overviewState.handleLottoEnabledChange}
                                checkboxIcon={overviewState.checkboxIcon}
                                checkboxCheckedIcon={overviewState.checkboxCheckedIcon}
                                username={overviewState.lottoCommittedUsername}
                                lotteryRetailerId={store.lottery_reseller_id}
                                password={overviewState.lottoForm.password}
                                passwordVisible={overviewState.lottoPasswordVisible}
                                passwordRevealing={overviewState.lottoPasswordRevealing}
                                onEditLottoPassword={overviewState.handleEditLottoPassword}
                                onRevealLottoPassword={overviewState.handleRevealLottoPassword}
                                onScratchersBulkSync={overviewState.handleScratchersBulkSync}
                                scratcherBulkSyncLoading={overviewState.scratcherBulkSyncLoading}
                                lottoLastSeenLabel={overviewState.lottoLastSeenLabel}
                                lottoPasswordLastRotatedLabel={overviewState.lottoPasswordLastRotatedLabel}
                            />
                        </StoreDetailsRenderProfiler>

                        <StoreDetailsRenderProfiler id="AgentConfigurationCard">
                            <AgentConfigurationCard
                                isAgentLoading={overviewState.isAgentLoading}
                                hasAgentDeviceId={overviewState.hasAgentDeviceId}
                                agentDeviceId={overviewState.agentDeviceId}
                                changingDevice={overviewState.changingDevice}
                                onChangeDevice={overviewState.handleChangeDevice}
                                onRetryVerifoneLogin={overviewState.handleRetryVerifoneLogin}
                                canRetryVerifoneLogin={overviewState.canRetryVerifoneLogin}
                                retryingVerifoneLogin={overviewState.retryingVerifoneLogin}
                                verifoneLoginRetryNeedsSave={overviewState.verifoneLoginRetryNeedsSave}
                                control={overviewState.agentConfigForm.control}
                                setValue={overviewState.agentConfigForm.setValue}
                                availableExtensions={overviewState.availableExtensions}
                                checkboxIcon={overviewState.checkboxIcon}
                                checkboxCheckedIcon={overviewState.checkboxCheckedIcon}
                                showSyncGamesToPos={showSyncGamesToPos}
                                syncGamesToPosEnabled={overviewState.syncGamesToPosEnabled}
                                syncGamesToPosUpdating={overviewState.syncGamesToPosUpdating}
                                onSyncGamesToPosChange={overviewState.handleSyncGamesToPosChange}
                                posType={store.pos_type}
                                selectedPosType={selectedPosType}
                                posTypeOptions={posTypes}
                                onPosTypeChange={setSelectedPosType}
                                posEnabled={overviewState.posEnabled}
                                onPosEnabledChange={overviewState.setPosEnabled}
                                onFetchLatestLogs={overviewState.handleFetchLatestLogs}
                                onDownloadLogs={overviewState.handleDownloadLogs}
                                onSave={overviewState.handleSaveAgentConfig}
                                saving={overviewState.saving}
                                isValid={overviewState.agentConfigForm.isValid}
                                lastFetchedLabel={overviewState.agentLastFetchedLabel}
                                showLastSeen={overviewState.showLastSeen}
                                lastSeenAt={overviewState.agentLastSeenAt}
                                lastSeenLabel={overviewState.agentLastSeenLabel}
                                heartbeatIntervalMinutes={overviewState.agentConfig?.config.Heartbeat?.IntervalMinutes}
                            />
                        </StoreDetailsRenderProfiler>
                    </Stack>
                </Grid>
            </Grid>

            <LottoCredentialsDialog
                open={overviewState.lottoDialogOpen}
                editMode={overviewState.lottoEditMode}
                username={overviewState.lottoForm.username}
                password={overviewState.lottoForm.password}
                lotteryRetailerId={overviewState.lottoForm.lotteryRetailerId}
                saving={overviewState.lottoSaving}
                passwordRevealing={overviewState.lottoPasswordRevealing}
                passwordVisible={overviewState.lottoPasswordVisible}
                onChangeEditMode={overviewState.setLottoEditMode}
                onChangeUsername={overviewState.setLottoUsername}
                onChangePassword={overviewState.setLottoPassword}
                onChangeLotteryRetailerId={overviewState.setLottoRetailerId}
                onRevealPassword={overviewState.handleRevealLottoPassword}
                onCancel={overviewState.onCloseLottoDialog}
                onSubmit={overviewState.handleSaveLottoSetup}
            />

            <LottoBackfillDialog
                open={overviewState.lottoBackfillDialogOpen}
                days={overviewState.lottoBackfillDays}
                loading={overviewState.lottoBackfillLoading}
                title={overviewState.lottoBackfillMode === 'scratchers' ? 'Backfill Scratcher Data' : 'Sync Lotto Data'}
                description={
                    overviewState.lottoBackfillMode === 'scratchers'
                        ? 'Enter how many days of scratcher history to sync through today.'
                        : 'Enter how many days of scratcher data to sync through today.'
                }
                showForceFetch={
                    overviewState.lottoBackfillMode === 'scratchers' &&
                    overviewState.lottoBackfillShowForceFetch
                }
                forceFetch={overviewState.lottoBackfillForceFetch}
                onDaysChange={overviewState.setLottoBackfillDays}
                onForceFetchChange={overviewState.setLottoBackfillForceFetch}
                onSubmit={overviewState.handleLottoBackfillSubmit}
                onCancel={overviewState.handleCloseLottoBackfill}
            />

            <StoreLogsDialog
                open={overviewState.logsDialogOpen}
                storeId={store.id}
                onClose={overviewState.onCloseLogsDialog}
            />
        </Box>
    );
};

export default React.memo(StoreDetailsDisplay);
